import React, { useEffect, useState, useMemo } from 'react';
import { Button, Card, CardContent, CardActions, Stack, TextField, Typography, Box, Checkbox, FormControlLabel } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { translate as t } from 'hooks/useTranslations';
import CategoryPicker from './CategoryPicker';
import FieldTypePicker from './FieldTypePicker';
import FieldTypeFields from './FieldTypeFields';
import { FieldTypeEnum, CreateFieldRequest, FieldOption, Field } from '../_types';
import errorHelper from 'helpers/errorHelper';
import { ServerErrorResponse } from 'data_sources/constants';
import { parseDateValue } from '../../_utils/fieldInputHelpers';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(utc);
dayjs.extend(customParseFormat);

const parseDateStrict = (v: any) => {
  if (v == null || v === '') return null;
  if (dayjs.isDayjs(v)) return v.isValid() ? v : null;
  // accept typed variants but enforce format strictly
  const d = dayjs(v, ['MM/DD/YYYY', 'M/D/YYYY', 'M/DD/YYYY', 'MM/D/YYYY'], true);
  return d.isValid() ? d : null;
};

// Step 1 validation schema
const createStep1Schema = () =>
  Yup.object().shape({
    name: Yup.string().required(t('performanceReporting.fieldBank.form.errors.fieldNameRequired')),
    description: Yup.string()
      .required(t('performanceReporting.fieldBank.form.errors.descriptionRequired'))
      .max(300, 'Description must be at most 300 characters'),
    fieldCategoryId: Yup.number().nullable().required(t('performanceReporting.fieldBank.form.errors.categoryRequired')),
    fieldType: Yup.string().required(t('performanceReporting.fieldBank.form.errors.dataTypeRequired')),
    isEisScorable: Yup.boolean(),
  });

// Step 2 validation schema
const createStep2Schema = () =>
  Yup.object().shape({
    name: Yup.string().required(t('performanceReporting.fieldBank.form.errors.fieldNameRequired')),
    description: Yup.string()
      .required(t('performanceReporting.fieldBank.form.errors.descriptionRequired'))
      .max(300, 'Description must be at most 300 characters'),
    fieldCategoryId: Yup.number().nullable().required(t('performanceReporting.fieldBank.form.errors.categoryRequired')),
    fieldType: Yup.string().required(t('performanceReporting.fieldBank.form.errors.dataTypeRequired')),
    isEisScorable: Yup.boolean(),
    label: Yup.string().required(t('performanceReporting.fieldBank.form.errors.labelRequired')),
    fieldOptions: Yup.array(),
    defaultValues: Yup.mixed<any>()
      .nullable()
      // Number: allow empty, else only whole numbers (form value may be string or string[])
      .when('fieldType', {
        is: 'Number',
        then: (schema) =>
          schema.test(
            'number-validation',
            t('performanceReporting.fieldBank.form.errors.wholeNumbersOnly'),
            (v) => {
              const val = Array.isArray(v) ? v[0] : v;
              return val == null || val === '' || /^\d*$/.test(String(val));
            }
          ),
      })
      // Date: Dayjs | null, valid, within range 01/01/1900–12/31/2199
      .when('fieldType', {
        is: 'Date',
        then: (schema) =>
          schema.test('date-validation', '', function (v) {
            const val = Array.isArray(v) ? v[0] : v;
            if (val == null || val === '') return true;
            const d = parseDateStrict(val);
            if (!d) {
              return this.createError({
                message: t('performanceReporting.fieldBank.form.errors.invalidDate'),
              });
            }
            const min = dayjs('1900-01-01');
            const max = dayjs('2199-12-31');
            if (d.isBefore(min, 'day') || d.isAfter(max, 'day')) {
              return this.createError({
                message: t('performanceReporting.fieldBank.form.errors.dateOutOfRange'),
              });
            }
            return true;
          }),
      })
      // Time: Dayjs | null, valid, HH:MM 24h, minutes 0–59, hours 0–23
      .when('fieldType', {
        is: 'Time',
        then: (schema) =>
          schema.test('time-validation', t('performanceReporting.fieldBank.form.errors.invalidTime'), (v) => {
            const val = Array.isArray(v) ? v[0] : v;
            if (val == null || val === '') return true;
            const d = dayjs.isDayjs(val) ? val : dayjs(val);
            if (!d.isValid()) return false;
            const h = d.hour();
            const m = d.minute();
            if (h < 0 || h > 23) return false;
            if (m < 0 || m > 59) return false;
            return true;
          }),
      }),
  });

const initialState = {
  name: '',
  description: '',
  fieldCategoryId: null as number | null,
  fieldType: '' as FieldTypeEnum | '',
  label: '',
  defaultValues: null as any, // Dayjs | null | string | boolean
  dropdownOptions: [] as string[],
  status: 'Draft' as 'Draft' | 'Active',
  isEisScorable: false as boolean,
};

// Helper function to transform field data for editing
// Helper function to transform field data for editing
const transformFieldForEditing = (fieldData: Field) => {
  const dv = fieldData.defaultValues;
  const arr = Array.isArray(dv) ? dv : dv != null && dv !== '' ? [String(dv)] : [];
  const first = arr[0];

  const baseData: any = {
    name: fieldData.name || '',
    description: fieldData.description || '',
    fieldCategoryId: fieldData.fieldCategoryId,
    fieldType: fieldData.fieldType,
    label: fieldData.label || '',
    defaultValues:
      fieldData.fieldType === 'Date'
        ? parseDateValue(first)
        : fieldData.fieldType === 'Time'
          ? first
            ? dayjs.utc(first).local()
            : null
          : fieldData.fieldType === 'Boolean'
            ? first !== null && first !== undefined && first !== ''
              ? first
              : 'false'
            : fieldData.fieldType === 'MultiSelect'
              ? arr
              : first ?? null,
    status: fieldData.status,
    isEisScorable: fieldData.isEisScorable || false,
  };

  if (fieldData.fieldType === 'SingleSelect' || fieldData.fieldType === 'MultiSelect') {
    let fieldOptions: FieldOption[] = [];
    if (fieldData.fieldOptions && fieldData.fieldOptions.length > 0) {
      fieldOptions = fieldData.fieldOptions
        .filter((opt) => opt.status !== 'Inactive')
        .sort((a, b) => (a.rank || 0) - (b.rank || 0))
        .map((opt) => ({ ...opt, label: opt.label || '' }));
    }
    return { ...baseData, fieldOptions };
  }

  return baseData;
};

const FieldForm = ({
  onSave,
  onCancel,
  initialValues,
  serverErrors,
  setServerErrors,
  fieldId,
  isEditing = false,
}: any) => {
  const [step, setStep] = useState(1);
  const [isSubmittingDraft, setIsSubmittingDraft] = useState(false);
  const [isSubmittingPublish, setIsSubmittingPublish] = useState(false);
  const [schemaErrors, setSchemaErrors] = useState<any>(null);
  const formRef = React.useRef<HTMLFormElement>(null);

  // Transform initial values for editing
  const transformedInitialValues = useMemo(() => {
    if (initialValues && isEditing) {
      return transformFieldForEditing(initialValues);
    }
    return initialValues || initialState;
  }, [initialValues, isEditing]);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: transformedInitialValues,
    resolver: yupResolver(step === 1 ? createStep1Schema() : createStep2Schema()),
    mode: 'onSubmit',
  });

  const isDraft = watch('status') === 'Draft';
  const isEditMode = !!initialValues; // Edit mode when initialValues are provided
  const shouldDisableField = isEditMode && !isDraft; // Only disable in edit mode when not draft
  const shouldShowSaveAsDraft = isDraft; // Show "Save as Draft" if field is draft OR in edit mode

  useEffect(() => {
    if (step === 2 && !getValues('label')) {
      setValue('label', getValues('name'));
    }
    // eslint-disable-next-line
  }, [step]);

  // Reset defaultValues when fieldType changes
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'fieldType') {
        // Set appropriate default value based on field type
        if (value.fieldType === 'Boolean') {
          setValue('defaultValues', 'false');
        } else if (value.fieldType === 'MultiSelect') {
          setValue('defaultValues', []);
        } else {
          setValue('defaultValues', null);
        }
      }
      // Clear schema errors when fieldOptions change (user is fixing the issue)
      if (name === 'fieldOptions' && schemaErrors?.fieldOptions) {
        setSchemaErrors(null);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue, schemaErrors]);

  const handleNext = async () => {
    // Clear any previous errors when navigating
    if (setServerErrors) {
      setServerErrors(undefined);
    }
    setSchemaErrors(null);

    const valid = await trigger(undefined, { shouldFocus: true });
    if (valid) setStep(2);
  };

  const handleBack = () => {
    // Clear any previous errors when navigating
    if (setServerErrors) {
      setServerErrors(undefined);
    }
    setSchemaErrors(null);

    setStep(1);
  };

  const onSubmit = async (data: any) => {
    // Clear any previous errors
    if (setServerErrors) {
      setServerErrors(undefined);
    }
    setSchemaErrors(null);

    // Trigger validation for the current step
    const isValid = await trigger();
    if (!isValid) {
      return; // Don't proceed if validation fails
    }

    const status = data.status || 'Active';

    // Additional validation for SingleSelect/MultiSelect fields when publishing
    if (status === 'Active' && (data.fieldType === 'SingleSelect' || data.fieldType === 'MultiSelect')) {
      const validOptions = data.fieldOptions?.filter((opt: FieldOption) => opt.label && opt.label.trim() !== '') || [];

      // Check for minimum 2 options
      if (validOptions.length < 2) {
        setSchemaErrors({
          fieldOptions: { message: 'At least 2 options with labels are required when publishing' },
        });
        return;
      }

      // Check for duplicate labels
      const labels = validOptions.map((opt: FieldOption) => opt.label.trim().toLowerCase());
      const uniqueLabels = new Set(labels);
      if (labels.length !== uniqueLabels.size) {
        setSchemaErrors({
          fieldOptions: { message: 'Please remove duplicate options before publishing' },
        });
        return;
      }

      // Check for empty labels
      const hasEmptyLabels = data.fieldOptions?.some((opt: FieldOption) => !opt.label || opt.label.trim() === '');
      if (hasEmptyLabels) {
        setSchemaErrors({
          fieldOptions: { message: 'Field option labels cannot be empty when publishing' },
        });
        return;
      }
    }

    // Additional validation for editing existing field options on active fields
    if (isEditing && initialValues?.status === 'Active' && (data.fieldType === 'SingleSelect' || data.fieldType === 'MultiSelect')) {
      // Check if user is trying to modify existing option labels
      const originalOptions = initialValues?.fieldOptions || [];
      const currentOptions = data.fieldOptions || [];

      for (const currentOption of currentOptions) {
        // Find the corresponding original option by ID
        const originalOption = originalOptions.find(
          (orig: FieldOption) => orig.id === currentOption.id && orig.id !== null
        );

        if (originalOption && originalOption.label !== currentOption.label) {
          setSchemaErrors({
            fieldOptions: {
              message:
                'Field option labels cannot be modified when the field is active. Delete the option and create a new one instead.',
            },
          });
          return;
        }
      }
    }

    if (status === 'Draft') {
      setIsSubmittingDraft(true);
    } else {
      setIsSubmittingPublish(true);
    }

    try {
      // Format fieldOptions for API
      // Build defaultValue as string[] for API
      let defaultValueIso: string[] = [];

      if (data.fieldType === 'Date') {
        const raw = data.defaultValues;
        const val = Array.isArray(raw) ? raw[0] : raw;
        if (val != null && val !== '') {
          const d = dayjs.isDayjs(val) ? val : dayjs(val);
          if (d.isValid()) {
            defaultValueIso = [d.startOf('day').utc().toISOString()];
          }
        }
      } else if (data.fieldType === 'Time') {
        const raw = data.defaultValues;
        const val = Array.isArray(raw) ? raw[0] : raw;
        if (val != null && val !== '') {
          const t = dayjs.isDayjs(val) ? val : dayjs(val);
          if (t.isValid()) {
            const utcTime = t.utc();
            defaultValueIso = [
              dayjs
                .utc('1970-01-01T00:00:00Z')
                .hour(utcTime.hour())
                .minute(utcTime.minute())
                .second(0)
                .millisecond(0)
                .toISOString(),
            ];
          }
        }
      } else if (data.fieldType === 'Boolean') {
        const raw = data.defaultValues;
        const val = Array.isArray(raw) ? raw[0] : raw;
        defaultValueIso = [val === true || val === 'true' ? 'true' : 'false'];
      } else if (data.fieldType === 'MultiSelect') {
        defaultValueIso = Array.isArray(data.defaultValues) ? data.defaultValues.filter(Boolean) : [];
      } else {
        // SingleSelect, Text, Number, Lookup, Narrative: single value
        const raw = data.defaultValues;
        const val = Array.isArray(raw) ? raw[0] : raw;
        if (raw != null && raw !== '' && val != null && val !== '') {
          defaultValueIso = [String(val)];
        }
      }

      // Format fieldOptions for API (unchanged from your code)
      let formattedFieldOptions: any[] | undefined = undefined;
      if ((data.fieldType === 'SingleSelect' || data.fieldType === 'MultiSelect') && data.fieldOptions) {
        formattedFieldOptions = data.fieldOptions.map((option: FieldOption) => {
          if (!option.id || option.id === null) {
            return { label: option.label, id: null };
          }
          return { id: option.id, ...option };
        });
      }

      const formattedData: CreateFieldRequest = {
        name: data.name,
        label: data.label,
        description: data.description,
        defaultValues: defaultValueIso,
        fieldType: data.fieldType,
        fieldCategoryId: data.fieldCategoryId,
        status: status,
        fieldOptions: formattedFieldOptions,
        isEisScorable: data.isEisScorable || false,
      };

      await onSave(formattedData, status);
    } catch (error: any) {
      // All errors will be handled by the parent component (EditField)
      // Field option errors will come back through setServerErrors prop
      throw error;
    } finally {
      if (status === 'Draft') {
        setIsSubmittingDraft(false);
      } else {
        setIsSubmittingPublish(false);
      }
    }
  };

  return (
    <Card
      sx={{ maxWidth: 800, mx: 'auto', width: '100%' }}
      className='lex-form'
    >
      <form
        ref={formRef}
        onSubmit={handleSubmit(onSubmit)}
        autoComplete='off'
      >
        <CardContent>
          {errorHelper.renderAllErrors(serverErrors, schemaErrors)}
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
            <Typography
              variant='body2'
              color='text.secondary'
              fontWeight={500}
            >
              {step === 1
                ? t('performanceReporting.fieldBank.form.step1Of2')
                : t('performanceReporting.fieldBank.form.step2Of2')}
            </Typography>
          </Box>
          <Stack spacing={3}>
            {step === 1 && (
              <>
                <Controller
                  name='name'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('performanceReporting.fieldBank.form.fieldName')}
                      required
                      fullWidth
                      error={!!errors.name}
                      helperText={(errors.name?.message as string) || ''}
                      disabled={shouldDisableField}
                      sx={
                        shouldDisableField
                          ? {
                              '& .MuiInputBase-root': {
                                backgroundColor: 'grey.100',
                              },
                              '& .MuiInputBase-input': {
                                color: 'text.disabled',
                              },
                            }
                          : {}
                      }
                    />
                  )}
                />
                <Controller
                  name='description'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('performanceReporting.fieldBank.form.description')}
                      required
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={6}
                      inputProps={{ maxLength: 300 }}
                      error={!!errors.description}
                      helperText={(errors.description?.message as string) || `${field.value.length}/300`}
                    />
                  )}
                />
                <Controller
                  name='fieldCategoryId'
                  control={control}
                  render={({ field }) => (
                    <CategoryPicker
                      id='fieldCategoryId'
                      label={t('performanceReporting.fieldBank.form.category')}
                      value={field.value ? [String(field.value)] : []}
                      onChange={(val) => field.onChange(val[0] ? Number(val[0]) : null)}
                      required
                      error={!!errors.fieldCategoryId}
                      helperText={(errors.fieldCategoryId?.message as string) || ''}
                      allowMultiple={false}
                      disabled={shouldDisableField}
                    />
                  )}
                />
                <Controller
                  name='fieldType'
                  control={control}
                  render={({ field }) => (
                    <FieldTypePicker
                      id='fieldType'
                      label={t('performanceReporting.fieldBank.form.dataType')}
                      value={field.value ? [field.value] : []}
                      onChange={(val) => field.onChange(val[0] || '')}
                      required
                      error={!!errors.fieldType}
                      helperText={(errors.fieldType?.message as string) || ''}
                      allowMultiple={false}
                      disabled={shouldDisableField}
                    />
                  )}
                />
                <Controller
                  name='isEisScorable'
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value || false}
                          disabled={false}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant='body1'>
                            {t('performanceReporting.fieldBank.form.eisScoreable')}
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                />
              </>
            )}
            {step === 2 && (
              <>
                <Controller
                  name='name'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('performanceReporting.fieldBank.form.fieldName')}
                      required
                      fullWidth
                      error={!!errors.name}
                      helperText={(errors.name?.message as string) || ''}
                      disabled={true}
                      sx={{
                        '& .MuiInputBase-root': {
                          backgroundColor: 'grey.100',
                        },
                        '& .MuiInputBase-input': {
                          color: 'text.disabled',
                        },
                      }}
                    />
                  )}
                />
                <FieldTypeFields
                  control={control}
                  errors={errors}
                  fieldType={watch('fieldType')}
                  getValues={getValues}
                  setValue={setValue}
                  fieldId={fieldId}
                  isEditing={isEditing}
                  isDraft={isDraft}
                />
                <Controller
                  name='status'
                  control={control}
                  render={({ field }) => (
                    <input
                      type='hidden'
                      {...field}
                    />
                  )}
                />
              </>
            )}
          </Stack>
        </CardContent>
        <CardActions sx={{ justifyContent: 'space-between', px: 3, pb: 3 }}>
          {step === 1 && (
            <Box sx={{ display: 'flex', width: '100%', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant='outlined'
                color='neutral'
                onClick={onCancel}
              >
                {t('performanceReporting.fieldBank.form.cancel')}
              </Button>
              <Button
                variant='contained'
                color='primary'
                onClick={handleNext}
              >
                {t('performanceReporting.fieldBank.form.continue')}
              </Button>
            </Box>
          )}
          {step === 2 && (
            <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', gap: 2 }}>
              <Button
                color='neutral'
                onClick={handleBack}
              >
                {t('performanceReporting.fieldBank.form.back')}
              </Button>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {shouldShowSaveAsDraft && (
                  <Button
                    variant='outlined'
                    color='primary'
                    type='button'
                    onMouseDown={(e) => {
                      e.preventDefault();
                      // Don't change form status immediately - let parent handle it
                      const formData = getValues();
                      onSubmit({ ...formData, status: 'Draft' });
                    }}
                    disabled={isSubmittingDraft || isSubmittingPublish}
                    loading={isSubmittingDraft}
                    loadingPosition='end'
                  >
                    {t('performanceReporting.fieldBank.form.saveAsDraft')}
                  </Button>
                )}
                <Button
                  variant='contained'
                  color='primary'
                  type='button'
                  onMouseDown={(e) => {
                    e.preventDefault();
                    // Don't change form status immediately - let parent handle it
                    const formData = getValues();
                    onSubmit({ ...formData, status: 'Active' });
                  }}
                  loading={isSubmittingPublish}
                  loadingPosition='end'
                  disabled={isSubmittingDraft || isSubmittingPublish}
                >
                  {t('performanceReporting.fieldBank.form.publishField')}
                </Button>
              </Box>
            </Box>
          )}
        </CardActions>
      </form>
    </Card>
  );
};

export default FieldForm;
